import { Box, Chip, Stack, Switch, Tooltip, Typography } from "@mui/material";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

// Shared pieces — presentational and cache-update alike — for any screen that renders a
// WhatsApp thread — originally written once in WhatsAppInboxPage.jsx (the admin-wide Inbox)
// and duplicated a second time in CreatorWhatsAppTab.jsx (Payment History's per-creator
// tab). Pulled out here so a third screen (PlanWhatsAppPage, the plan-scoped view) doesn't
// need a third copy, and WhatsAppInboxPage now imports from here instead of keeping its own
// definitions.

// You can have multiple WhatsApp business numbers, same as multiple Instagram accounts —
// so each thread is colored by which of our numbers it's on. Numbers aren't known ahead of
// time, so the color is derived deterministically from the number itself rather than a
// hardcoded map.
const WA_PALETTE = ["#25D366", "#128C7E", "#075E54", "#34B7F1", "#00A884", "#008069", "#0F969C"];
export function getNumberColor(key) {
  const str = String(key || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return WA_PALETTE[hash % WA_PALETTE.length];
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function istDateKey(d) {
  const ist = new Date(d.getTime() + IST_OFFSET_MS);
  return `${ist.getUTCFullYear()}-${ist.getUTCMonth()}-${ist.getUTCDate()}`;
}

export function formatTime(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
}

export function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  if (istDateKey(d) === istDateKey(now)) return "Today";
  if (istDateKey(d) === istDateKey(new Date(now.getTime() - 86400000))) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

export function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
}

export function groupMessagesByDate(messages) {
  const groups = {};
  for (const msg of messages) {
    const label = formatDateLabel(msg.sentAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
  }
  return groups;
}

// The name resolution below already prefers influencer data, but the customer's actual
// WhatsApp number was never shown anywhere — neither the list nor the thread header — so
// there was no way to tell who a bare "customerName" fallback (or an unmatched conversation)
// actually was without opening WhatsApp itself. customerWaId is the one field guaranteed to
// exist on every conversation (it's the join key — see WhatsappConversation's schema
// comment), so it's the reliable source here, formatted the way a person would read it
// rather than as a raw digit string. Most numbers here are Indian (country code 91 + a
// 10-digit number = 12 digits); anything else falls back to a generic "assume the extra
// digits beyond 10 are the country code" split rather than guessing wrong.
export function formatWaId(waId) {
  const digits = String(waId || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length > 10) {
    const ccLen = digits.length - 10;
    return `+${digits.slice(0, ccLen)} ${digits.slice(ccLen)}`;
  }
  return `+${digits}`;
}

export function hasRealName(conv) {
  return Boolean(conv?.influencerDbId?.userName || conv?.influencerDbId?.username || conv?.customerName);
}

export function conversationDisplayName(conv) {
  return conv?.influencerDbId?.userName || conv?.influencerDbId?.username || conv?.customerName || formatWaId(conv?.customerWaId) || "Unknown";
}

// react-query cache updaters for a single thread's socket-driven live updates — identical
// regardless of which screen owns the query, since only the query key (scoped by the
// caller) differs between the admin Inbox and a plan-scoped thread.
export function upsertThreadMessage(oldData, conversationId, message) {
  if (!oldData?.conversation || oldData.conversation.conversationId !== conversationId) return oldData;
  const alreadyExists = oldData.conversation.messages.some((m) => m.waMessageId === message.waMessageId);
  if (alreadyExists) return oldData;
  return {
    ...oldData,
    conversation: {
      ...oldData.conversation,
      lastMessageAt: message.sentAt,
      messages: [...oldData.conversation.messages, message],
    },
  };
}

// Delivery-status ticks (sent -> delivered -> read) arrive as a separate socket event from
// the message itself — see whatsappWebhook.js's status handler, which emits
// 'whatsapp-message-status' rather than piggybacking on 'new-whatsapp-message'.
export function upsertThreadMessageStatus(oldData, conversationId, waMessageId, status) {
  if (!oldData?.conversation || oldData.conversation.conversationId !== conversationId) return oldData;
  return {
    ...oldData,
    conversation: {
      ...oldData.conversation,
      messages: oldData.conversation.messages.map((m) => (m.waMessageId === waMessageId ? { ...m, status } : m)),
    },
  };
}

// Delivery tick — WhatsApp-native visual language (single/double/blue check).
export function StatusTick({ status }) {
  if (!status) return null;
  if (status === "read") return <DoneAllRoundedIcon sx={{ fontSize: 13, color: "#53BDEB" }} />;
  if (status === "delivered") return <DoneAllRoundedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }} />;
  if (status === "sent") return <DoneRoundedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }} />;
  return null;
}

// AI follow-up (backend: helpers/whatsappAi.js). Only conversations that were started from a
// Plan bulk send ever have a status — everything else renders nothing here.
const AI_STATUS = {
  awaiting_reply: { short: "AI waiting", color: "#B36B00", bg: "rgba(250,153,5,0.14)" },
  in_progress: { short: "AI chatting", color: "#1D4ED8", bg: "rgba(59,130,246,0.12)" },
  complete: { short: "AI done", color: "#09AC31", bg: "rgba(9,172,49,0.12)" },
  handoff: { short: "Needs you", color: "#E34C14", bg: "rgba(227,76,20,0.10)" },
};

export function AiStatusChip({ status, paused }) {
  const meta = AI_STATUS[status];
  if (!meta) return null;
  const showPaused = paused && status !== "complete";
  return (
    <Chip
      size="small"
      icon={<SmartToyOutlinedIcon sx={{ fontSize: "12px !important", color: "inherit !important" }} />}
      label={showPaused ? "AI paused" : meta.short}
      sx={{
        height: 18,
        bgcolor: showPaused ? "#F1F1F1" : meta.bg,
        color: showPaused ? "#666" : meta.color,
        fontWeight: 700,
        fontSize: 10,
        flexShrink: 0,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
}

// Thread-header strip: what the AI has collected so far, what is still missing, and (when
// `onTogglePause` is given) the per-thread pause switch.
export function AiProgressBar({ progress, onTogglePause, pausing }) {
  if (!progress) return null;
  const meta = AI_STATUS[progress.status];
  if (!meta) return null;

  let summary;
  if (progress.status === "complete") summary = `AI finished — ${progress.answered} detail${progress.answered === 1 ? "" : "s"} collected and saved`;
  else if (progress.status === "handoff") summary = `AI handed this chat to you${progress.handoffReason ? `: ${progress.handoffReason}` : ""}`;
  else if (progress.status === "awaiting_reply") summary = "AI will take over when the creator replies";
  else summary = `AI collecting details — ${progress.answered}/${progress.total} done`;

  const canPause = onTogglePause && progress.status !== "complete";
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ px: 2.5, py: 0.75, bgcolor: meta.bg, borderBottom: "1px solid rgba(0,0,0,0.05)", flexShrink: 0 }}
    >
      <SmartToyOutlinedIcon sx={{ fontSize: 15, color: meta.color }} />
      <Tooltip
        title={progress.missing?.length ? `Still needed: ${progress.missing.join(" · ")}` : ""}
        placement="bottom-start"
        disableHoverListener={!progress.missing?.length}
      >
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, fontWeight: 700, color: meta.color, flex: 1, minWidth: 0 }}>
          {progress.paused && progress.status !== "complete" ? "AI paused — " : ""}
          {summary}
        </Typography>
      </Tooltip>
      {canPause && (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 11, color: "#6B7280" }}>Pause AI</Typography>
          <Switch size="small" checked={Boolean(progress.paused)} disabled={pausing} onChange={(e) => onTogglePause(e.target.checked)} />
        </Stack>
      )}
    </Stack>
  );
}

export function MessageBubble({ message, accentColor }) {
  const isUs = message.isFromUs;
  const isSending = message._status === "sending";
  const isFailed = message._status === "failed";
  return (
    <Box sx={{ display: "flex", justifyContent: isUs ? "flex-end" : "flex-start", mb: 0.75, opacity: isSending ? 0.6 : 1 }}>
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "70%", md: "60%" },
          background: isFailed
            ? "rgba(229,57,53,0.12)"
            : isUs
            ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}CC 100%)`
            : "rgba(255,255,255,0.97)",
          color: isFailed ? "#E53935" : isUs ? "#fff" : "#1C1C2E",
          borderRadius: isUs ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          px: 2,
          py: 1.25,
          boxShadow: isUs ? `0 4px 16px ${accentColor}40` : "0 2px 8px rgba(0,0,0,0.07)",
          border: isFailed ? "1px solid rgba(229,57,53,0.3)" : isUs ? "none" : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {message.isAi && (
          <Typography sx={{ fontSize: 10, fontWeight: 800, fontFamily: "Quicksand", opacity: 0.75, mb: 0.25, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 0.4 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 11 }} /> AI
          </Typography>
        )}
        {message.type === "template" && (
          <Typography sx={{ fontSize: 10, fontWeight: 800, fontFamily: "Quicksand", opacity: 0.75, mb: 0.25, letterSpacing: 0.3 }}>
            TEMPLATE{message.templateName ? ` · ${message.templateName}` : ""}
          </Typography>
        )}
        {message.text ? (
          <Typography sx={{ fontSize: 13.5, fontFamily: "Quicksand", fontWeight: 600, lineHeight: 1.55, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
            {message.text}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, fontFamily: "Quicksand", fontStyle: "italic", opacity: 0.55 }}>
            [{message.type || "attachment"}]
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.4, mt: 0.5 }}>
          <Typography sx={{ fontSize: 10, fontFamily: "Quicksand", color: isUs ? "rgba(255,255,255,0.65)" : "#9CA3AF" }}>
            {formatTime(message.sentAt)}
          </Typography>
          {isUs && !isFailed && !isSending && <StatusTick status={message.status} />}
        </Box>
      </Box>
    </Box>
  );
}
